<template>
  <div class="page-content evolucion-consumos">
    <nav class="page-breadcrumb" id="breadcrumb">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">{{ breadcrumbA }}</li>
        <li class="breadcrumb-item active" aria-current="page">{{ breadcrumbB }}</li>
      </ol>
    </nav>

    <div class="row">
      <div class="col-md-12 grid-margin stretch-card">
        <div class="card">
          <div class="card-body header-card">
            <div>
              <h6 class="card-title mb-1">{{ breadcrumbB }}</h6>
              <p class="card-description mb-0">{{ descripcion }}</p>
            </div>
            <div class="header-actions">
              <span class="badge badge-light mr-2" v-if="ultimaActualizacion">
                Actualizado: {{ ultimaActualizacion }}
              </span>
              <button class="btn btn-primary btn-sm" :disabled="loading" @click="recargar">
                ACTUALIZAR DATOS
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="row">
      <div class="col-md-12 grid-margin stretch-card">
        <div class="card">
          <div class="card-body loading-panel">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>Cargando evolución de consumos</strong>
              <span>{{ progreso }}%</span>
            </div>
            <div class="progress progress-lg mb-2">
              <div
                class="progress-bar"
                role="progressbar"
                :style="{ width: progreso + '%' }"
                :aria-valuenow="progreso"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
            <small class="text-muted">{{ textoProgreso }}</small>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && errorGeneral" class="row">
      <div class="col-md-12 grid-margin stretch-card">
        <div class="card border-danger">
          <div class="card-body">
            <div class="alert alert-danger mb-0">
              <strong>No se pudo cargar el tablero.</strong> {{ errorGeneral }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="!loading && !errorGeneral">
      <div v-if="consultasFallidas > 0" class="row">
        <div class="col-md-12">
          <div class="alert alert-warning">
            Se cargó el tablero, pero {{ consultasFallidas }} consulta{{ consultasFallidas === 1 ? '' : 's' }} no respondieron.
            Los períodos afectados se muestran con la información disponible.
          </div>
        </div>
      </div>

      <!-- FILTRO GLOBAL DE SERVICIOS -->
      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card">
            <div class="card-body service-filter-card">
              <div>
                <div class="service-filter-title">QUÉ CONSUMOS QUIERO ANALIZAR</div>
                <div class="text-muted service-filter-help">
                  El cambio es instantáneo: ambos universos quedan calculados en memoria y no vuelve a consultar la API.
                </div>
              </div>
              <div class="btn-group btn-group-sm service-filter-buttons" role="group">
                <button
                  class="btn"
                  :class="consumptionMode === 'ALL' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="consumptionMode = 'ALL'"
                >TODOS LOS SERVICIOS</button>
                <button
                  class="btn"
                  :class="consumptionMode === 'MAIN' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="consumptionMode = 'MAIN'"
                >ALMUERZOS + CENAS + VIANDAS</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RESUMEN -->
      <div class="row summary-row">
        <div class="col-xl-3 col-md-6 grid-margin stretch-card">
          <div class="card summary-card">
            <div class="card-body">
              <div class="summary-label">CAT + SAL · MES ACTUAL</div>
              <div class="summary-value">{{ formatNumber(resumenActual.total) }}</div>
              <div v-if="resumenActual.partial" class="summary-sub text-muted">
                Acumulado al {{ formatDateDisplay(resumenActual.end) }}
              </div>
              <div v-else class="summary-sub" :class="variationClass(resumenActual.variacion)">
                {{ variationText(resumenActual.variacion) }} vs. mes anterior
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6 grid-margin stretch-card">
          <div class="card summary-card">
            <div class="card-body">
              <div class="summary-label">CAT · MES ACTUAL</div>
              <div class="summary-value">{{ formatNumber(resumenActual.cat) }}</div>
              <div class="summary-sub">{{ formatPercent(resumenActual.participacionCat) }} del total</div>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6 grid-margin stretch-card">
          <div class="card summary-card">
            <div class="card-body">
              <div class="summary-label">SAL · MES ACTUAL</div>
              <div class="summary-value">{{ formatNumber(resumenActual.sal) }}</div>
              <div class="summary-sub">{{ formatPercent(resumenActual.participacionSal) }} del total</div>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6 grid-margin stretch-card">
          <div class="card summary-card holiday-summary-card">
            <div class="card-body">
              <div class="summary-label">PROMEDIO POR FERIADO · 12 MESES</div>
              <div class="summary-value">{{ formatNumber(holidayStats.promedioTotal) }}</div>
              <div class="summary-sub">
                {{ holidayStats.cantidad }} feriado{{ holidayStats.cantidad === 1 ? '' : 's' }} con datos
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 12 MESES -->
      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card">
            <div class="card-body">
              <div class="section-title-row">
                <div>
                  <h6 class="card-title mb-1">EVOLUCIÓN MENSUAL · ÚLTIMOS 12 MESES</h6>
                  <p class="card-description mb-0">CAT y SAL juntos y por separado.</p>
                </div>
                <div class="legend-inline">
                  <span><i class="legend-dot total"></i> CAT + SAL</span>
                  <span><i class="legend-dot cat"></i> CAT</span>
                  <span><i class="legend-dot sal"></i> SAL</span>
                </div>
              </div>

              <div class="chart-wrap">
                <svg
                  v-if="monthlyChart.series.length"
                  class="line-chart"
                  :viewBox="'0 0 ' + monthlyChart.width + ' ' + monthlyChart.height"
                  preserveAspectRatio="none"
                  aria-label="Evolución mensual de consumos"
                >
                  <line
                    v-for="grid in monthlyChart.grid"
                    :key="'mg-' + grid.y"
                    :x1="monthlyChart.plot.left"
                    :x2="monthlyChart.width - monthlyChart.plot.right"
                    :y1="grid.y"
                    :y2="grid.y"
                    class="grid-line"
                  />
                  <text
                    v-for="grid in monthlyChart.grid"
                    :key="'mgt-' + grid.y"
                    :x="monthlyChart.plot.left - 10"
                    :y="grid.y + 4"
                    text-anchor="end"
                    class="axis-text"
                  >{{ grid.label }}</text>

                  <g v-for="label in monthlyChart.xLabels" :key="'mxl-' + label.index">
                    <line
                      :x1="label.x"
                      :x2="label.x"
                      :y1="monthlyChart.height - monthlyChart.plot.bottom"
                      :y2="monthlyChart.height - monthlyChart.plot.bottom + 5"
                      class="axis-tick"
                    />
                    <text
                      :x="label.x"
                      :y="monthlyChart.height - 14"
                      text-anchor="middle"
                      class="axis-text"
                    >{{ label.label }}</text>
                  </g>

                  <g v-for="serie in monthlyChart.series" :key="serie.key">
                    <path :d="serie.path" fill="none" :stroke="serie.color" stroke-width="3" class="series-path" />
                    <circle
                      v-for="point in serie.points"
                      :key="serie.key + '-' + point.index"
                      :cx="point.x"
                      :cy="point.y"
                      r="4"
                      :fill="serie.color"
                      class="chart-point"
                    >
                      <title>{{ point.tooltip }}</title>
                    </circle>
                  </g>
                </svg>
              </div>

              <div class="table-responsive mt-4">
                <table class="table table-hover compact-table">
                  <thead>
                    <tr>
                      <th>Mes</th>
                      <th class="text-right">CAT</th>
                      <th class="text-right">SAL</th>
                      <th class="text-right">CAT + SAL</th>
                      <th class="text-right">CAT %</th>
                      <th class="text-right">SAL %</th>
                      <th class="text-right">Variación total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in monthlyRows" :key="row.key">
                      <td><strong>{{ row.label }}</strong></td>
                      <td class="text-right">{{ row.failed ? '—' : formatNumber(row.cat) }}</td>
                      <td class="text-right">{{ row.failed ? '—' : formatNumber(row.sal) }}</td>
                      <td class="text-right"><strong>{{ row.failed ? '—' : formatNumber(row.total) }}</strong></td>
                      <td class="text-right">{{ row.failed ? '—' : formatPercent(row.participacionCat) }}</td>
                      <td class="text-right">{{ row.failed ? '—' : formatPercent(row.participacionSal) }}</td>
                      <td class="text-right" :class="variationClass(row.variacion)">{{ row.failed ? 'Sin datos' : variationText(row.variacion) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEMANAL 6 MESES -->
      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card">
            <div class="card-body">
              <div class="section-title-row">
                <div>
                  <h6 class="card-title mb-1">EVOLUCIÓN SEMANAL · ÚLTIMOS 6 MESES</h6>
                  <p class="card-description mb-0">Semanas de lunes a domingo. La semana actual se muestra hasta el día de hoy.</p>
                </div>
                <div class="legend-inline">
                  <span><i class="legend-dot total"></i> CAT + SAL</span>
                  <span><i class="legend-dot cat"></i> CAT</span>
                  <span><i class="legend-dot sal"></i> SAL</span>
                </div>
              </div>

              <div class="chart-wrap chart-wrap-tall">
                <svg
                  v-if="weeklyChart.series.length"
                  class="line-chart"
                  :viewBox="'0 0 ' + weeklyChart.width + ' ' + weeklyChart.height"
                  preserveAspectRatio="none"
                  aria-label="Evolución semanal de consumos"
                >
                  <line
                    v-for="grid in weeklyChart.grid"
                    :key="'wg-' + grid.y"
                    :x1="weeklyChart.plot.left"
                    :x2="weeklyChart.width - weeklyChart.plot.right"
                    :y1="grid.y"
                    :y2="grid.y"
                    class="grid-line"
                  />
                  <text
                    v-for="grid in weeklyChart.grid"
                    :key="'wgt-' + grid.y"
                    :x="weeklyChart.plot.left - 10"
                    :y="grid.y + 4"
                    text-anchor="end"
                    class="axis-text"
                  >{{ grid.label }}</text>

                  <g v-for="label in weeklyChart.xLabels" :key="'wxl-' + label.index">
                    <text
                      :x="label.x"
                      :y="weeklyChart.height - 14"
                      text-anchor="middle"
                      class="axis-text"
                    >{{ label.label }}</text>
                  </g>

                  <g v-for="serie in weeklyChart.series" :key="serie.key">
                    <path :d="serie.path" fill="none" :stroke="serie.color" stroke-width="2.5" class="series-path" />
                    <circle
                      v-for="point in serie.points"
                      :key="serie.key + '-' + point.index"
                      :cx="point.x"
                      :cy="point.y"
                      r="3.2"
                      :fill="serie.color"
                      class="chart-point"
                    >
                      <title>{{ point.tooltip }}</title>
                    </circle>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TENDENCIA POR DÍA -->
      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card">
            <div class="card-body">
              <div class="section-title-row day-filter-header">
                <div>
                  <h6 class="card-title mb-1">TENDENCIA POR DÍA DE SEMANA</h6>
                  <p class="card-description mb-0">Elegí qué días querés comparar semana contra semana.</p>
                </div>
                <div class="scope-selector btn-group btn-group-sm" role="group">
                  <button
                    class="btn"
                    :class="weeklyScope === 'TOTAL' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="weeklyScope = 'TOTAL'"
                  >CAT + SAL</button>
                  <button
                    class="btn"
                    :class="weeklyScope === 'CAT' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="weeklyScope = 'CAT'"
                  >CAT</button>
                  <button
                    class="btn"
                    :class="weeklyScope === 'SAL' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="weeklyScope = 'SAL'"
                  >SAL</button>
                </div>
              </div>

              <div class="weekday-filters">
                <label
                  v-for="day in weekdayOptions"
                  :key="day.id"
                  class="weekday-chip"
                  :class="{ active: selectedWeekdays.includes(day.id) }"
                >
                  <input type="checkbox" :value="day.id" v-model="selectedWeekdays" />
                  <span class="weekday-color" :style="{ backgroundColor: day.color }"></span>
                  {{ day.label }}
                </label>
              </div>

              <div v-if="selectedWeekdays.length === 0" class="empty-selection">
                Seleccioná al menos un día de la semana para ver su tendencia.
              </div>

              <div v-else class="chart-wrap chart-wrap-tall">
                <svg
                  class="line-chart"
                  :viewBox="'0 0 ' + weekdayChart.width + ' ' + weekdayChart.height"
                  preserveAspectRatio="none"
                  aria-label="Tendencia por día de la semana"
                >
                  <line
                    v-for="grid in weekdayChart.grid"
                    :key="'dg-' + grid.y"
                    :x1="weekdayChart.plot.left"
                    :x2="weekdayChart.width - weekdayChart.plot.right"
                    :y1="grid.y"
                    :y2="grid.y"
                    class="grid-line"
                  />
                  <text
                    v-for="grid in weekdayChart.grid"
                    :key="'dgt-' + grid.y"
                    :x="weekdayChart.plot.left - 10"
                    :y="grid.y + 4"
                    text-anchor="end"
                    class="axis-text"
                  >{{ grid.label }}</text>

                  <g v-for="label in weekdayChart.xLabels" :key="'dxl-' + label.index">
                    <text
                      :x="label.x"
                      :y="weekdayChart.height - 14"
                      text-anchor="middle"
                      class="axis-text"
                    >{{ label.label }}</text>
                  </g>

                  <g v-for="serie in weekdayChart.series" :key="serie.key">
                    <path :d="serie.path" fill="none" :stroke="serie.color" stroke-width="2.5" class="series-path" />
                    <circle
                      v-for="point in serie.points"
                      :key="serie.key + '-' + point.index"
                      :cx="point.x"
                      :cy="point.y"
                      r="3"
                      :fill="serie.color"
                      class="chart-point"
                    >
                      <title>{{ point.tooltip }}</title>
                    </circle>
                  </g>
                </svg>
              </div>

              <div class="table-responsive mt-4">
                <table class="table table-hover compact-table">
                  <thead>
                    <tr>
                      <th>Semana</th>
                      <th class="text-right">CAT</th>
                      <th class="text-right">SAL</th>
                      <th class="text-right">CAT + SAL</th>
                      <th
                        v-for="day in selectedWeekdayOptions"
                        :key="'th-' + day.id"
                        class="text-right"
                      >{{ day.short }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in weeklyRows" :key="row.key">
                      <td><strong>{{ row.label }}</strong></td>
                      <td class="text-right">{{ formatNumber(row.cat) }}</td>
                      <td class="text-right">{{ formatNumber(row.sal) }}</td>
                      <td class="text-right"><strong>{{ formatNumber(row.total) }}</strong></td>
                      <td
                        v-for="day in selectedWeekdayOptions"
                        :key="row.key + '-' + day.id"
                        class="text-right"
                      >
                        {{ row.days[day.id] ? formatNumber(scopeValue(row.days[day.id])) : '—' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FERIADOS -->
      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card">
            <div class="card-body">
              <div class="section-title-row">
                <div>
                  <h6 class="card-title mb-1">CONSUMOS EN FERIADOS · ÚLTIMOS 12 MESES</h6>
                  <p class="card-description mb-0">
                    Promedio real de consumo por feriado y comparación contra el promedio no feriado del mismo día de semana en los últimos 6 meses.
                  </p>
                </div>
              </div>

              <div class="row holiday-kpis">
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="mini-kpi">
                    <span>Promedio CAT + SAL</span>
                    <strong>{{ formatNumber(holidayStats.promedioTotal) }}</strong>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="mini-kpi">
                    <span>Promedio CAT</span>
                    <strong>{{ formatNumber(holidayStats.promedioCat) }}</strong>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="mini-kpi">
                    <span>Promedio SAL</span>
                    <strong>{{ formatNumber(holidayStats.promedioSal) }}</strong>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="mini-kpi">
                    <span>Impacto vs. día normal comparable</span>
                    <strong :class="variationClass(holidayStats.impactoVsNormal)">
                      {{ variationText(holidayStats.impactoVsNormal) }}
                    </strong>
                  </div>
                </div>
              </div>

              <div v-if="holidayRows.length === 0" class="empty-selection">
                No se encontraron feriados con datos dentro de los últimos 12 meses.
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover compact-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Feriado</th>
                      <th>Día</th>
                      <th class="text-right">CAT</th>
                      <th class="text-right">SAL</th>
                      <th class="text-right">CAT + SAL</th>
                      <th class="text-right">Día normal comparable</th>
                      <th class="text-right">Impacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="holiday in holidayRows" :key="holiday.date">
                      <td><strong>{{ formatDateDisplay(holiday.date) }}</strong></td>
                      <td>{{ holiday.label }}</td>
                      <td>{{ weekdayNameFromDate(holiday.date) }}</td>
                      <td class="text-right">{{ formatNumber(holiday.cat) }}</td>
                      <td class="text-right">{{ formatNumber(holiday.sal) }}</td>
                      <td class="text-right"><strong>{{ formatNumber(holiday.total) }}</strong></td>
                      <td class="text-right">{{ formatNumber(holiday.normalComparable) }}</td>
                      <td class="text-right" :class="variationClass(holiday.impacto)">{{ variationText(holiday.impacto) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

const API_CONSUMOS = 'https://consumos.foodservice.com.ar/api/totalEmpresas/';
const API_FERIADOS = 'https://auth.foodservice.com.ar/index_dev.php?type=feriados&access_token=1234567&anio=';
const API_FERIADOS_FALLBACK = 'https://aplicativos.firebaseio.com/feriados_data.json?print=pretty';
const CACHE_PREFIX = 'evolucion-consumos-v2:';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hora
const REQUEST_CONCURRENCY = 7;
const MONTHLY_REQUEST_CONCURRENCY = 1; // bloques mensuales en serie para no saturar la API

export default {
  name: 'EvolucionConsumos',

  data() {
    return {
      breadcrumbA: 'Inicio',
      breadcrumbB: 'EVOLUCION DE CONSUMOS',
      descripcion: 'Evolución mensual, semanal, por día de semana y comportamiento de consumos en feriados.',

      loading: true,
      progreso: 0,
      textoProgreso: 'Preparando períodos...',
      errorGeneral: '',
      consultasFallidas: 0,
      ultimaActualizacion: '',

      monthlyRaw: [],
      monthlyBlockRaw: [], // cada respuesta mensual de hasta 10 días queda guardada acá en memoria
      dailyRaw: [],
      feriados: [],
      holidayRaw: [],

      consumptionMode: 'ALL', // ALL = todos los servicios; MAIN = ALMUERZO + CENA + VIANDAS
      weeklyScope: 'TOTAL',
      selectedWeekdays: [1, 2, 3, 4, 5],

      weekdayOptions: [
        { id: 1, label: 'Lunes', short: 'Lun', color: '#2563eb' },
        { id: 2, label: 'Martes', short: 'Mar', color: '#7c3aed' },
        { id: 3, label: 'Miércoles', short: 'Mié', color: '#0891b2' },
        { id: 4, label: 'Jueves', short: 'Jue', color: '#059669' },
        { id: 5, label: 'Viernes', short: 'Vie', color: '#ca8a04' },
        { id: 6, label: 'Sábado', short: 'Sáb', color: '#ea580c' },
        { id: 0, label: 'Domingo', short: 'Dom', color: '#dc2626' }
      ]
    };
  },

  computed: {
    monthlyRows() {
      return this.monthlyRaw.map((row, index, rows) => {
        const totals = this.filteredTotals(row);
        const prevRaw = index > 0 ? rows[index - 1] : null;
        const prev = prevRaw ? this.filteredTotals(prevRaw) : null;
        return {
          ...row,
          ...totals,
          label: this.monthLabel(row.start),
          participacionCat: !row.failed && totals.total > 0 ? (totals.cat / totals.total) * 100 : 0,
          participacionSal: !row.failed && totals.total > 0 ? (totals.sal / totals.total) * 100 : 0,
          variacion: !row.failed && !row.incomplete && !row.partial && prevRaw && !prevRaw.failed && !prevRaw.incomplete && prev.total > 0
            ? ((totals.total - prev.total) / prev.total) * 100
            : null
        };
      });
    },

    resumenActual() {
      const current = this.monthlyRows.length ? this.monthlyRows[this.monthlyRows.length - 1] : null;
      if (!current) {
        return { total: 0, cat: 0, sal: 0, variacion: null, participacionCat: 0, participacionSal: 0, partial: false, end: this.formatDate(this.localToday()) };
      }
      return current;
    },

    weeklyRows() {
      const map = {};

      this.dailyRaw.forEach(day => {
        const date = this.parseLocalDate(day.date);
        const monday = this.mondayOf(date);
        const key = this.formatDate(monday);
        const weekDay = date.getDay();
        const totals = this.filteredTotals(day);

        if (!map[key]) {
          map[key] = {
            key,
            start: key,
            end: this.formatDate(this.addDays(monday, 6)),
            cat: 0,
            sal: 0,
            total: 0,
            days: {},
            failedDays: 0
          };
        }

        if (day.failed) {
          map[key].failedDays += 1;
          return;
        }

        map[key].cat += totals.cat;
        map[key].sal += totals.sal;
        map[key].total += totals.total;
        map[key].days[weekDay] = {
          cat: totals.cat,
          sal: totals.sal,
          total: totals.total,
          date: day.date
        };
      });

      return Object.values(map)
        .sort((a, b) => a.start.localeCompare(b.start))
        .map(row => ({
          ...row,
          label: this.weekLabel(row.start, row.end)
        }));
    },

    selectedWeekdayOptions() {
      return this.weekdayOptions.filter(day => this.selectedWeekdays.includes(day.id));
    },

    regularWeekdayAverages() {
      const holidaySet = new Set(this.feriados.map(item => item.date));
      const accum = {};

      this.weekdayOptions.forEach(day => {
        accum[day.id] = { total: 0, count: 0 };
      });

      this.dailyRaw.forEach(day => {
        if (day.failed || holidaySet.has(day.date)) return;
        const dow = this.parseLocalDate(day.date).getDay();
        const totals = this.filteredTotals(day);
        if (!accum[dow]) accum[dow] = { total: 0, count: 0 };
        accum[dow].total += totals.total;
        accum[dow].count += 1;
      });

      const result = {};
      Object.keys(accum).forEach(key => {
        result[key] = accum[key].count ? accum[key].total / accum[key].count : 0;
      });
      return result;
    },

    holidayRows() {
      return this.holidayRaw
        .map(row => {
          const totals = this.filteredTotals(row);
          const dow = this.parseLocalDate(row.date).getDay();
          const normalComparable = this.regularWeekdayAverages[dow] || 0;
          const impacto = normalComparable > 0 ? ((totals.total - normalComparable) / normalComparable) * 100 : null;
          return {
            ...row,
            ...totals,
            normalComparable,
            impacto
          };
        })
        .sort((a, b) => b.date.localeCompare(a.date));
    },

    holidayStats() {
      const rows = this.holidayRows;
      if (!rows.length) {
        return {
          cantidad: 0,
          promedioTotal: 0,
          promedioCat: 0,
          promedioSal: 0,
          impactoVsNormal: null
        };
      }

      const totals = rows.reduce((acc, row) => {
        acc.total += row.total;
        acc.cat += row.cat;
        acc.sal += row.sal;
        acc.normal += row.normalComparable || 0;
        acc.normalCount += row.normalComparable > 0 ? 1 : 0;
        return acc;
      }, { total: 0, cat: 0, sal: 0, normal: 0, normalCount: 0 });

      const avgTotal = totals.total / rows.length;
      const avgNormal = totals.normalCount ? totals.normal / totals.normalCount : 0;

      return {
        cantidad: rows.length,
        promedioTotal: avgTotal,
        promedioCat: totals.cat / rows.length,
        promedioSal: totals.sal / rows.length,
        impactoVsNormal: avgNormal > 0 ? ((avgTotal - avgNormal) / avgNormal) * 100 : null
      };
    },

    monthlyChart() {
      const rows = this.monthlyRows;
      return this.buildLineChart(
        rows,
        [
          { key: 'total', label: 'CAT + SAL', color: '#334155', value: row => row.failed ? null : row.total },
          { key: 'cat', label: 'CAT', color: '#2563eb', value: row => row.failed ? null : row.cat },
          { key: 'sal', label: 'SAL', color: '#10b981', value: row => row.failed ? null : row.sal }
        ],
        row => this.shortMonthLabel(row.start),
        row => row.label,
        12,
        true
      );
    },

    weeklyChart() {
      const rows = this.weeklyRows;
      return this.buildLineChart(
        rows,
        [
          { key: 'total', label: 'CAT + SAL', color: '#334155', value: row => row.total },
          { key: 'cat', label: 'CAT', color: '#2563eb', value: row => row.cat },
          { key: 'sal', label: 'SAL', color: '#10b981', value: row => row.sal }
        ],
        row => this.shortWeekLabel(row.start),
        row => row.label,
        9
      );
    },

    weekdayChart() {
      const rows = this.weeklyRows;
      const definitions = this.selectedWeekdayOptions.map(day => ({
        key: 'day-' + day.id,
        label: day.label,
        color: day.color,
        value: row => row.days[day.id] ? this.scopeValue(row.days[day.id]) : null
      }));

      return this.buildLineChart(
        rows,
        definitions,
        row => this.shortWeekLabel(row.start),
        row => row.label,
        9,
        true
      );
    }
  },

  methods: {
    async cargarDatos(force = false) {
      this.loading = true;
      this.progreso = 0;
      this.textoProgreso = 'Preparando períodos...';
      this.errorGeneral = '';
      this.consultasFallidas = 0;
      this.monthlyRaw = [];
      this.monthlyBlockRaw = [];
      this.dailyRaw = [];
      this.holidayRaw = [];

      try {
        if (force) this.clearCache();

        const today = this.localToday();
        const todayIso = this.formatDate(today);
        const monthlyRanges = this.getLastCalendarMonths(today, 12);
        const sixMonthsStart = new Date(today.getFullYear(), today.getMonth() - 5, 1, 12, 0, 0);
        const dailyDates = this.dateRange(sixMonthsStart, today);
        const twelveMonthsStart = this.parseLocalDate(monthlyRanges[0].start);

        this.textoProgreso = 'Cargando feriados...';
        this.feriados = await this.loadHolidays(twelveMonthsStart, today, force);
        this.progreso = 5;

        const holidayDates = this.feriados
          .map(item => item.date)
          .filter(date => date >= this.formatDate(twelveMonthsStart) && date <= todayIso);

        const dailySet = new Set(dailyDates.map(date => this.formatDate(date)));
        const extraHolidayDates = holidayDates.filter(date => !dailySet.has(date));

        // IMPORTANTE: la evolución mensual nunca consulta un mes completo.
        // Cada mes se parte en rangos de hasta 10 días y cada bloque se guarda en monthlyBlockRaw.
        const monthTasks = [];
        monthlyRanges.forEach(range => {
          const blocks = this.splitRangeIntoBlocks(range.start, range.end, 10);
          blocks.forEach((block, index) => {
            monthTasks.push({
              type: 'month-block',
              key: range.key + '-b' + (index + 1),
              monthKey: range.key,
              monthStart: range.start,
              monthEnd: range.end,
              start: block.start,
              end: block.end,
              blockIndex: index + 1,
              blockCount: blocks.length,
              partial: range.partial
            });
          });
        });

        const dayTasks = dailyDates.map(date => {
          const iso = this.formatDate(date);
          return { type: 'day', key: iso, start: iso, end: iso };
        });

        const holidayTasks = extraHolidayDates.map(date => ({
          type: 'holiday', key: date, start: date, end: date
        }));

        let completed = 0;
        const totalTasks = monthTasks.length + dayTasks.length + holidayTasks.length || 1;
        const updateProgress = task => {
          completed += 1;
          this.progreso = 5 + Math.round((completed / totalTasks) * 93);
          this.textoProgreso = this.progressText(task, completed, totalTasks);
        };

        this.textoProgreso = 'Cargando evolución mensual en bloques de hasta 10 días...';

        // Los bloques mensuales se procesan en serie para proteger totalEmpresas de rangos pesados simultáneos.
        const monthResults = await this.mapWithConcurrency(monthTasks, MONTHLY_REQUEST_CONCURRENCY, async task => {
          const result = await this.fetchTotalsSafe(task.start, task.end, force);
          const row = { ...task, ...result };
          this.monthlyBlockRaw.push(row);
          updateProgress(task);
          return row;
        });

        // Consolidación en memoria: suma los bloques de 10 días y forma los 12 totales mensuales.
        this.monthlyBlockRaw.sort((a, b) => a.start.localeCompare(b.start));
        this.monthlyRaw = this.consolidateMonthlyBlocks(monthlyRanges, monthResults);

        this.textoProgreso = 'Cargando detalle diario para semanas y días de semana...';
        const detailTasks = [...dayTasks, ...holidayTasks];
        const detailResults = await this.mapWithConcurrency(detailTasks, REQUEST_CONCURRENCY, async task => {
          const result = await this.fetchTotalsSafe(task.start, task.end, force);
          updateProgress(task);
          return { ...task, ...result };
        });

        const daily = detailResults
          .filter(item => item.type === 'day')
          .map(item => ({
            date: item.start,
            all: item.all,
            main: item.main,
            failed: item.failed
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

        const extraHolidayMap = {};
        detailResults
          .filter(item => item.type === 'holiday')
          .forEach(item => {
            extraHolidayMap[item.start] = item;
          });

        const dailyMap = {};
        daily.forEach(item => {
          dailyMap[item.date] = item;
        });

        this.dailyRaw = daily;
        this.holidayRaw = this.feriados
          .filter(item => item.date >= this.formatDate(twelveMonthsStart) && item.date <= todayIso)
          .map(item => {
            const totals = dailyMap[item.date] || extraHolidayMap[item.date];
            return {
              date: item.date,
              label: item.label || 'Feriado',
              type: item.type || '',
              all: totals ? totals.all : this.emptyMetrics(),
              main: totals ? totals.main : this.emptyMetrics(),
              failed: totals ? totals.failed : true
            };
          })
          .filter(item => !item.failed);

        this.progreso = 100;
        this.textoProgreso = 'Datos listos.';
        this.ultimaActualizacion = this.formatDateTime(new Date());
      } catch (error) {
        console.error('Error cargando EVOLUCION DE CONSUMOS:', error);
        this.errorGeneral = error && error.message ? error.message : 'Error inesperado al consultar las APIs.';
      } finally {
        this.loading = false;
      }
    },

    async recargar() {
      await this.cargarDatos(true);
    },

    async fetchTotalsSafe(start, end, force) {
      try {
        const totals = await this.fetchTotals(start, end, force);
        return { ...totals, failed: false };
      } catch (error) {
        console.warn('No respondió totalEmpresas:', start, end, error);
        this.consultasFallidas += 1;
        return { all: this.emptyMetrics(), main: this.emptyMetrics(), failed: true };
      }
    },

    async fetchTotals(start, end, force = false) {
      const cacheKey = CACHE_PREFIX + 'consumos:' + start + ':' + end;
      if (!force) {
        const cached = this.getCache(cacheKey);
        if (cached) return cached;
      }

      const response = await axios.get(API_CONSUMOS + start + '/' + end);
      const totals = this.extractTotals(response.data);
      this.setCache(cacheKey, totals);
      return totals;
    },

    extractTotals(payload) {
      let catBlock = null;
      let salBlock = null;

      const inspect = item => {
        if (!item || typeof item !== 'object') return;
        if (item.CAT && typeof item.CAT === 'object') catBlock = item.CAT;
        if (item.SAL && typeof item.SAL === 'object') salBlock = item.SAL;
      };

      if (Array.isArray(payload)) {
        payload.forEach(inspect);
      } else {
        inspect(payload);
        if (payload && Array.isArray(payload.data)) payload.data.forEach(inspect);
      }

      const allCat = this.sumConsumptionBlock(catBlock || {}, 'ALL');
      const allSal = this.sumConsumptionBlock(salBlock || {}, 'ALL');
      const mainCat = this.sumConsumptionBlock(catBlock || {}, 'MAIN');
      const mainSal = this.sumConsumptionBlock(salBlock || {}, 'MAIN');

      return {
        // Se calculan ambos universos una sola vez. El selector visual no dispara nuevas llamadas.
        all: { cat: allCat, sal: allSal, total: allCat + allSal },
        main: { cat: mainCat, sal: mainSal, total: mainCat + mainSal }
      };
    },

    sumConsumptionBlock(block, mode = 'ALL') {
      if (!block || typeof block !== 'object') return 0;
      return Object.keys(block).reduce((sum, key) => {
        const company = block[key];
        if (!company || typeof company !== 'object') return sum;
        const services = company.Servicios || {};
        return sum + (mode === 'MAIN'
          ? this.sumMainServices(services)
          : this.sumNumericLeaves(services));
      }, 0);
    },

    sumMainServices(services) {
      if (!services || typeof services !== 'object') return 0;
      const accepted = new Set(['ALMUERZO', 'ALMUERZOS', 'CENA', 'CENAS', 'VIANDA', 'VIANDAS']);

      return Object.keys(services).reduce((sum, key) => {
        const normalizedKey = this.normalizeServiceKey(key);
        if (!accepted.has(normalizedKey)) return sum;
        return sum + this.sumServiceValue(services[key]);
      }, 0);
    },

    normalizeServiceKey(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase();
    },

    sumServiceValue(serviceValue) {
      if (typeof serviceValue === 'number') {
        return Number.isFinite(serviceValue) ? serviceValue : 0;
      }

      if (typeof serviceValue === 'string') {
        const normalized = serviceValue.trim().replace(/\s/g, '').replace(',', '.');
        const numeric = Number(normalized);
        return Number.isFinite(numeric) ? numeric : 0;
      }

      if (serviceValue && typeof serviceValue === 'object') {
        const candidate = serviceValue.cantidad !== undefined
          ? serviceValue.cantidad
          : serviceValue.cantidad_categoria;
        const numeric = Number(candidate);
        return Number.isFinite(numeric) ? numeric : 0;
      }

      return 0;
    },

    sumNumericLeaves(value) {
      if (!value || typeof value !== 'object') return 0;

      return Object.keys(value).reduce((sum, key) => {
        return sum + this.sumServiceValue(value[key]);
      }, 0);
    },

    emptyMetrics() {
      return { cat: 0, sal: 0, total: 0 };
    },

    filteredTotals(row) {
      if (!row) return this.emptyMetrics();
      const metrics = this.consumptionMode === 'MAIN' ? row.main : row.all;
      if (metrics && typeof metrics === 'object') {
        return {
          cat: Number(metrics.cat || 0),
          sal: Number(metrics.sal || 0),
          total: Number(metrics.total || 0)
        };
      }

      // Compatibilidad defensiva con datos cacheados de una versión anterior.
      return {
        cat: Number(row.cat || 0),
        sal: Number(row.sal || 0),
        total: Number(row.total || 0)
      };
    },

    addMetrics(target, source) {
      target.cat += Number(source && source.cat || 0);
      target.sal += Number(source && source.sal || 0);
      target.total += Number(source && source.total || 0);
      return target;
    },

    splitRangeIntoBlocks(start, end, blockDays = 10) {
      const blocks = [];
      let cursor = this.parseLocalDate(start);
      const last = this.parseLocalDate(end);

      while (cursor <= last) {
        const blockEnd = this.addDays(cursor, blockDays - 1);
        const safeEnd = blockEnd > last ? last : blockEnd;
        blocks.push({
          start: this.formatDate(cursor),
          end: this.formatDate(safeEnd)
        });
        cursor = this.addDays(safeEnd, 1);
      }

      return blocks;
    },

    consolidateMonthlyBlocks(monthlyRanges, monthResults) {
      return monthlyRanges.map(range => {
        const blocks = monthResults
          .filter(item => item.monthKey === range.key)
          .sort((a, b) => a.start.localeCompare(b.start));

        const all = this.emptyMetrics();
        const main = this.emptyMetrics();
        let failedBlocks = 0;

        blocks.forEach(block => {
          if (block.failed) {
            failedBlocks += 1;
            return;
          }
          this.addMetrics(all, block.all);
          this.addMetrics(main, block.main);
        });

        return {
          key: range.key,
          start: range.start,
          end: range.end,
          partial: range.partial,
          all,
          main,
          blocks: blocks.length,
          failedBlocks,
          failed: blocks.length === 0 || failedBlocks === blocks.length,
          incomplete: failedBlocks > 0 && failedBlocks < blocks.length
        };
      }).sort((a, b) => a.start.localeCompare(b.start));
    },

    async loadHolidays(fromDate, toDate, force = false) {
      const years = [];
      for (let year = fromDate.getFullYear(); year <= toDate.getFullYear(); year++) {
        years.push(year);
      }

      try {
        const batches = await Promise.all(years.map(async year => {
          const cacheKey = CACHE_PREFIX + 'feriados:' + year;
          if (!force) {
            const cached = this.getCache(cacheKey);
            if (cached) return cached;
          }

          const response = await axios.get(API_FERIADOS + year);
          const normalized = this.normalizeHolidayPayload(response.data, year);
          this.setCache(cacheKey, normalized);
          return normalized;
        }));

        return this.uniqueHolidays([].concat(...batches));
      } catch (error) {
        console.warn('Falló API principal de feriados; usando respaldo Firebase.', error);
        const response = await axios.get(API_FERIADOS_FALLBACK);
        const normalized = this.normalizeHolidayPayload(response.data, null)
          .filter(item => years.includes(Number(item.date.slice(0, 4))));
        return this.uniqueHolidays(normalized);
      }
    },

    normalizeHolidayPayload(payload, fallbackYear) {
      let source = payload;
      if (source && Array.isArray(source.data)) source = source.data;
      if (source && source.feriados && Array.isArray(source.feriados)) source = source.feriados;

      if (!Array.isArray(source) && source && typeof source === 'object') {
        source = Object.values(source).filter(Boolean);
      }
      if (!Array.isArray(source)) return [];

      return source.map(item => {
        if (!item) return null;

        let date = item.date || item.fecha || '';
        if (!date && item.anio && item.mes && item.dia) {
          date = String(item.anio) + '-' + this.pad(item.mes) + '-' + this.pad(item.dia);
        }
        if (!date && fallbackYear && item.mes && item.dia) {
          date = String(fallbackYear) + '-' + this.pad(item.mes) + '-' + this.pad(item.dia);
        }

        date = this.normalizeDateString(date);
        if (!date) return null;

        return {
          date,
          label: item.label || item.nombre || item.descripcion || item.motivo || 'Feriado',
          type: item.type || item.tipo || ''
        };
      }).filter(Boolean);
    },

    uniqueHolidays(rows) {
      const map = {};
      rows.forEach(row => {
        if (!map[row.date]) map[row.date] = row;
      });
      return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
    },

    normalizeDateString(value) {
      if (!value) return '';
      const text = String(value).trim();

      const isoMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (isoMatch) {
        return isoMatch[1] + '-' + this.pad(isoMatch[2]) + '-' + this.pad(isoMatch[3]);
      }

      const compactMatch = text.match(/^(\d{4})(\d{2})(\d{2})$/);
      if (compactMatch) {
        return compactMatch[1] + '-' + compactMatch[2] + '-' + compactMatch[3];
      }

      const latinMatch = text.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (latinMatch) {
        return latinMatch[3] + '-' + this.pad(latinMatch[2]) + '-' + this.pad(latinMatch[1]);
      }

      return '';
    },

    async mapWithConcurrency(items, concurrency, mapper) {
      const results = new Array(items.length);
      let cursor = 0;

      const worker = async () => {
        while (true) {
          const current = cursor;
          cursor += 1;
          if (current >= items.length) return;
          results[current] = await mapper(items[current], current);
        }
      };

      const workers = [];
      const count = Math.min(concurrency, items.length || 1);
      for (let i = 0; i < count; i++) workers.push(worker());
      await Promise.all(workers);
      return results;
    },

    progressText(task, completed, total) {
      if (task.type === 'month-block') {
        return 'Mes ' + this.monthLabel(task.monthStart) + ' · bloque ' + task.blockIndex + '/' + task.blockCount +
          ' (' + this.formatDateDisplay(task.start) + ' al ' + this.formatDateDisplay(task.end) + ') · ' +
          completed + ' de ' + total + ' consultas';
      }
      if (task.type === 'holiday') {
        return 'Feriado ' + this.formatDateDisplay(task.start) + ' · ' + completed + ' de ' + total + ' consultas';
      }
      return 'Día ' + this.formatDateDisplay(task.start) + ' · ' + completed + ' de ' + total + ' consultas';
    },

    getLastCalendarMonths(today, count) {
      const ranges = [];
      for (let offset = count - 1; offset >= 0; offset--) {
        const start = new Date(today.getFullYear(), today.getMonth() - offset, 1, 12, 0, 0);
        const naturalEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0, 12, 0, 0);
        const end = naturalEnd > today ? today : naturalEnd;
        ranges.push({
          key: start.getFullYear() + '-' + this.pad(start.getMonth() + 1),
          start: this.formatDate(start),
          end: this.formatDate(end),
          partial: naturalEnd > today
        });
      }
      return ranges;
    },

    dateRange(startDate, endDate) {
      const dates = [];
      let cursor = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 12, 0, 0);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 12, 0, 0);

      while (cursor <= end) {
        dates.push(new Date(cursor.getTime()));
        cursor = this.addDays(cursor, 1);
      }
      return dates;
    },

    localToday() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
    },

    parseLocalDate(value) {
      if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0);
      }
      const parts = String(value).split('-').map(Number);
      return new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1, 12, 0, 0);
    },

    mondayOf(date) {
      const local = this.parseLocalDate(date);
      const day = local.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      return this.addDays(local, diff);
    },

    addDays(date, amount) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount, 12, 0, 0);
    },

    pad(value) {
      return Number(value) < 10 ? '0' + Number(value) : String(value);
    },

    formatDate(date) {
      return date.getFullYear() + '-' + this.pad(date.getMonth() + 1) + '-' + this.pad(date.getDate());
    },

    formatDateDisplay(value) {
      const date = this.parseLocalDate(value);
      return this.pad(date.getDate()) + '/' + this.pad(date.getMonth() + 1) + '/' + date.getFullYear();
    },

    formatDateTime(date) {
      return this.pad(date.getDate()) + '/' + this.pad(date.getMonth() + 1) + '/' + date.getFullYear() +
        ' ' + this.pad(date.getHours()) + ':' + this.pad(date.getMinutes());
    },

    monthLabel(value) {
      const date = this.parseLocalDate(value);
      const label = new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric' }).format(date);
      return label.charAt(0).toUpperCase() + label.slice(1);
    },

    shortMonthLabel(value) {
      const date = this.parseLocalDate(value);
      const month = new Intl.DateTimeFormat('es-AR', { month: 'short' }).format(date).replace('.', '');
      return month.charAt(0).toUpperCase() + month.slice(1) + ' ' + String(date.getFullYear()).slice(-2);
    },

    shortWeekLabel(value) {
      const date = this.parseLocalDate(value);
      return this.pad(date.getDate()) + '/' + this.pad(date.getMonth() + 1);
    },

    weekLabel(start, end) {
      return this.formatDateDisplay(start) + ' al ' + this.formatDateDisplay(end);
    },

    weekdayNameFromDate(value) {
      const day = this.parseLocalDate(value).getDay();
      const option = this.weekdayOptions.find(item => item.id === day);
      return option ? option.label : '';
    },

    scopeValue(data) {
      if (!data) return 0;
      if (this.weeklyScope === 'CAT') return data.cat || 0;
      if (this.weeklyScope === 'SAL') return data.sal || 0;
      return data.total || 0;
    },

    buildLineChart(rows, seriesDefinitions, xLabelFn, tooltipLabelFn, maxXLabels = 10, preserveNulls = false) {
      const width = 1000;
      const height = 330;
      const plot = { left: 72, right: 24, top: 22, bottom: 48 };
      const plotWidth = width - plot.left - plot.right;
      const plotHeight = height - plot.top - plot.bottom;

      const values = [];
      rows.forEach(row => {
        seriesDefinitions.forEach(def => {
          const value = def.value(row);
          if (value !== null && value !== undefined && Number.isFinite(Number(value))) {
            values.push(Number(value));
          }
        });
      });

      const maxValueRaw = Math.max(1, ...values);
      const maxValue = this.niceMax(maxValueRaw);
      const xFor = index => plot.left + (rows.length <= 1 ? plotWidth / 2 : (index / (rows.length - 1)) * plotWidth);
      const yFor = value => plot.top + plotHeight - (Number(value || 0) / maxValue) * plotHeight;

      const grid = [];
      const gridSteps = 5;
      for (let i = 0; i <= gridSteps; i++) {
        const value = (maxValue / gridSteps) * i;
        grid.push({
          value,
          y: yFor(value),
          label: this.formatCompact(value)
        });
      }

      const labelStep = Math.max(1, Math.ceil(rows.length / maxXLabels));
      const xLabels = rows
        .map((row, index) => ({ index, x: xFor(index), label: xLabelFn(row) }))
        .filter(item => item.index % labelStep === 0 || item.index === rows.length - 1);

      const series = seriesDefinitions.map(def => {
        const points = rows.map((row, index) => {
          const raw = def.value(row);
          if (raw === null || raw === undefined || !Number.isFinite(Number(raw))) return null;
          const value = Number(raw);
          return {
            index,
            x: xFor(index),
            y: yFor(value),
            value,
            tooltip: def.label + ' · ' + tooltipLabelFn(row) + ': ' + this.formatNumber(value)
          };
        });

        const validPoints = points.filter(Boolean);
        let path = '';

        if (preserveNulls) {
          let started = false;
          points.forEach(point => {
            if (!point) {
              started = false;
              return;
            }
            path += (started ? ' L ' : ' M ') + point.x + ' ' + point.y;
            started = true;
          });
        } else {
          path = validPoints.map((point, index) => (index === 0 ? 'M ' : 'L ') + point.x + ' ' + point.y).join(' ');
        }

        return {
          key: def.key,
          label: def.label,
          color: def.color,
          path,
          points: validPoints
        };
      });

      return { width, height, plot, grid, xLabels, series };
    },

    niceMax(value) {
      if (!value || value <= 0) return 1;
      const exponent = Math.floor(Math.log10(value));
      const magnitude = Math.pow(10, exponent);
      const normalized = value / magnitude;
      let nice;
      if (normalized <= 1) nice = 1;
      else if (normalized <= 2) nice = 2;
      else if (normalized <= 5) nice = 5;
      else nice = 10;
      return nice * magnitude;
    },

    formatNumber(value) {
      return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Number(value || 0));
    },

    formatCompact(value) {
      const number = Number(value || 0);
      try {
        return new Intl.NumberFormat('es-AR', { notation: 'compact', maximumFractionDigits: 1 }).format(number);
      } catch (e) {
        if (number >= 1000000) return (number / 1000000).toFixed(1) + 'M';
        if (number >= 1000) return (number / 1000).toFixed(0) + 'k';
        return String(Math.round(number));
      }
    },

    formatPercent(value) {
      if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
      return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number(value)) + '%';
    },

    variationText(value) {
      if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
      const number = Number(value);
      const prefix = number > 0 ? '+' : '';
      return prefix + this.formatPercent(number);
    },

    variationClass(value) {
      if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'text-muted';
      if (Number(value) > 0) return 'text-success';
      if (Number(value) < 0) return 'text-danger';
      return 'text-muted';
    },

    getCache(key) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.createdAt || Date.now() - parsed.createdAt > CACHE_TTL_MS) {
          localStorage.removeItem(key);
          return null;
        }
        return parsed.value;
      } catch (e) {
        return null;
      }
    },

    setCache(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify({ createdAt: Date.now(), value }));
      } catch (e) {
        // Si localStorage está lleno o bloqueado, el tablero sigue funcionando sin caché.
      }
    },

    clearCache() {
      try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.indexOf(CACHE_PREFIX) === 0) keys.push(key);
        }
        keys.forEach(key => localStorage.removeItem(key));
      } catch (e) {
        // Sin acción: la recarga sigue funcionando aunque no pueda limpiar caché.
      }
    }
  },

  async mounted() {
    await this.cargarDatos(false);
  }
};
</script>

<style scoped>
.evolucion-consumos {
  --total-color: #334155;
  --cat-color: #2563eb;
  --sal-color: #10b981;
  --border-soft: #e5e7eb;
  --bg-soft: #f8fafc;
  --text-soft: #64748b;
}

.header-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.loading-panel {
  padding: 28px;
}

.service-filter-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  border-left: 4px solid var(--cat-color);
}

.service-filter-title {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.05em;
  color: #0f172a;
  margin-bottom: 4px;
}

.service-filter-help {
  font-size: 12px;
}

.service-filter-buttons {
  flex-wrap: wrap;
}

.summary-row .card {
  min-height: 140px;
}

.summary-card {
  border: 1px solid var(--border-soft);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.summary-label {
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}

.summary-value {
  color: #0f172a;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
}

.summary-sub {
  margin-top: 9px;
  font-size: 12px;
}

.holiday-summary-card {
  background: linear-gradient(180deg, #fffdf5 0%, #ffffff 100%);
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 18px;
}

.legend-inline {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--text-soft);
}

.legend-inline span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.legend-dot.total { background: var(--total-color); }
.legend-dot.cat { background: var(--cat-color); }
.legend-dot.sal { background: var(--sal-color); }

.chart-wrap {
  width: 100%;
  min-height: 310px;
  overflow-x: auto;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: #fff;
  padding: 10px;
}

.chart-wrap-tall {
  min-height: 335px;
}

.line-chart {
  width: 100%;
  min-width: 780px;
  height: 315px;
  display: block;
}

.grid-line {
  stroke: #e5e7eb;
  stroke-width: 1;
  stroke-dasharray: 4 5;
}

.axis-tick {
  stroke: #94a3b8;
  stroke-width: 1;
}

.axis-text {
  fill: #64748b;
  font-size: 11px;
  font-family: Arial, sans-serif;
}

.series-path {
  vector-effect: non-scaling-stroke;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.chart-point {
  vector-effect: non-scaling-stroke;
  stroke: #fff;
  stroke-width: 1.5;
  cursor: pointer;
}

.compact-table th {
  font-size: 11px;
  white-space: nowrap;
}

.compact-table td {
  font-size: 12px;
  white-space: nowrap;
}

.day-filter-header {
  align-items: center;
}

.weekday-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.weekday-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  margin: 0;
  border: 1px solid var(--border-soft);
  border-radius: 20px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
}

.weekday-chip input {
  display: none;
}

.weekday-chip.active {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  font-weight: 600;
}

.weekday-color {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.scope-selector {
  white-space: nowrap;
}

.empty-selection {
  border: 1px dashed #cbd5e1;
  background: var(--bg-soft);
  color: var(--text-soft);
  padding: 24px;
  border-radius: 8px;
  text-align: center;
}

.holiday-kpis {
  margin-top: 6px;
  margin-bottom: 8px;
}

.mini-kpi {
  height: 100%;
  padding: 14px 16px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: var(--bg-soft);
}

.mini-kpi span {
  display: block;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 8px;
}

.mini-kpi strong {
  display: block;
  color: #0f172a;
  font-size: 23px;
  line-height: 1.1;
}

@media (max-width: 991px) {
  .header-card,
  .section-title-row {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    justify-content: flex-start;
  }

  .scope-selector {
    align-self: flex-start;
  }
}
</style>